import type { Expand } from "@pdfking/structs"

import { RecordAssign } from "./RecordAssign"

type OptionalKeys<T> = { [K in keyof T]-?: EmptyRecord extends Pick<T, K> ? K : never }[keyof T]

// Widen A's values with the matching optional value from B, keeping A's own
// `readonly`/`?` modifiers (the mapping stays homomorphic).
type Widen<A, B> = { [K in keyof A]: A[K] | Exclude<B[K & keyof B], undefined> }

// Same shape TypeScript gives `{ ...a, ...b }`: a key merely *optional* in B
// cannot un-require it in A — the merged value is `A[K] | B[K]` and stays
// required. Anything else turns `RecordMerge(defaults, partial)` into an
// all-optional record, which is never what the caller means.
type Merge<A, B> = Omit<A, keyof B> &
  Omit<B, OptionalKeys<B>> &
  Widen<Pick<A, Extract<OptionalKeys<B>, keyof A>>, B> &
  Pick<B, Exclude<OptionalKeys<B>, keyof A>>

type EmptyRecord = { [K in never]: never }

type MergeAll<T extends Record<string, unknown>[]> = Expand<
  T extends [infer First, ...infer Rest]
    ? Rest extends Record<string, unknown>[]
      ? Merge<First, MergeAll<Rest>>
      : First
    : EmptyRecord
>

/**
 * Merges multiple records into one, preserving all keys.
 * Properties from later records override those from earlier ones for duplicate keys.
 *
 * @param args - Records to merge
 * @returns Merged record with all keys from all inputs
 *
 * @example
 * ```ts
 * import { RecordMerge } from "@pdfking/tfunc"
 *
 * RecordMerge({ a: 1 }, { b: 2 }) // { a: 1, b: 2 }
 * RecordMerge({ a: 1, b: 2 }, { b: 3 }) // { a: 1, b: 3 }
 * RecordMerge({ a: 1 }, { b: 2 }, { c: 3 }) // { a: 1, b: 2, c: 3 }
 * RecordMerge() // {}
 * ```
 */
export const RecordMerge = <T extends Record<string, unknown>[]>(...args: T): MergeAll<T> =>
  RecordAssign({}, ...args) as MergeAll<T>
